import { trackApplicationEvent, ProductContext, UserContext, AnalyticsContext, ErrorContext } from './analytics';

// Component props interface for the analytics helpers
export interface ComponentWithAnalyticsProps {
  application?: ProductContext;
  userInfo?: UserContext;
  user?: UserContext;
  site?: string;
  location?: string;
  entryPoint?: string;
  flowContext?: string;
}

// Error context interface for the helper functions
export interface ErrorContextParams {
  validation_errors?: string[];
  error_message?: string | string[];
  [key: string]: any; // Allow additional properties
}

// Options interface for createApiTrackingCallback
export interface ApiTrackingOptions {
  onSuccessCallback?: () => void;
  onErrorCallback?: (errors: any) => void;
}

// API tracking callback interface
export interface ApiTrackingCallback {
  onSuccess: () => void;
  onError: (errors: any) => void;
}

/**
 * Helper to track events with automatic context gathering from component props
 * @param component - Class component instance with props
 * @param eventName - Analytics event name
 * @param errorContext - Additional context to merge (validation_errors, error_message, etc.)
 */
export const trackWithContext = (
  component: { props: ComponentWithAnalyticsProps },
  eventName: string,
  errorContext: ErrorContextParams = {}
): void => {
  const { validation_errors, error_message, ...productAdditionalContext } = errorContext;
  
  const separatedErrorContext: ErrorContext = {};
  if (validation_errors !== undefined) {
    separatedErrorContext.validation_errors = validation_errors;
  }
  if (error_message !== undefined) {
    separatedErrorContext.error_message = error_message;
  }

  trackApplicationEvent(
    eventName,
    { 
      ...component.props.application, 
      state: component.props.site || component.props.location, 
      ...productAdditionalContext 
    },
    component.props.userInfo || component.props.user || {},
    { entry_point: component.props.entryPoint, flow_context: component.props.flowContext },
    separatedErrorContext
  );
}; 

/**
 * Creates a tracking callback object for API requests with consistent success/error tracking
 * @param component - Class component instance with props
 * @param successEventName - Analytics event name for successful API response
 * @param errorEventName - Analytics event name for API error response
 * @param options - Optional configuration object
 * @returns Callback object with onSuccess and onError methods
 */
export const createApiTrackingCallback = (
  component: { props: ComponentWithAnalyticsProps },
  successEventName: string,
  errorEventName: string,
  options: ApiTrackingOptions = {}
): ApiTrackingCallback => ({
  onSuccess: (): void => {
    trackWithContext(component, successEventName);
    // Run custom callback after tracking (important for navigation that depends on updated state)
    if (options.onSuccessCallback) {
      options.onSuccessCallback();
    }
  },
  onError: (errors: any): void => {
    let errorMessage: string | string[] | undefined = undefined;
    if (errors && errors.errors) {
      errorMessage = errors.errors;
    }
    trackWithContext(component, errorEventName, { error_message: errorMessage });
    // Run custom error callback after tracking
    if (options.onErrorCallback) {
      options.onErrorCallback(errors);
    }
  }
}); 